import { useCallback } from "react";
import { useUserOrg } from "@/hooks/useUserOrg";
import { useOrgMemberCount } from "@/hooks/useOrgMemberCount";
import {
  computeOrgProfileStrength,
  isReadyToPublish,
  type OrgProfileStrength,
} from "@/lib/orgProfileStrength";

export interface UseOrgProfileStrength {
  strength: OrgProfileStrength | null;
  isReady: boolean;
  isPublished: boolean;
  isLoading: boolean;
  orgId: string | null;
  /** Current members of this org — the Team section's input. */
  memberCount: number;
  /**
   * Refetch the inputs and resolve the strength they produce.
   *
   * Resolves `null` when either input could not be refreshed. Callers that must
   * report a number the admin just earned — the onboarding handoff — should show
   * nothing rather than the stale value in that case.
   */
  refresh: () => Promise<OrgProfileStrength | null>;
}

/**
 * Profile strength for the org the admin is currently administering.
 *
 * Both inputs are shared caches — `useUserOrg`'s keyed org row and
 * `useOrgMemberCount` — so every surface that renders a percentage reads the
 * same two values, and `refresh()` moves all of them at once. That is the whole
 * point: before this hook's inputs were shared, the onboarding modal, the admin
 * feed card and the profile editor each held a private copy of the org row and
 * could report different numbers for the same organization.
 *
 * `useUserOrg` selects "*", so profile_data and is_published are already on the
 * row; its exported `UserOrg` interface is narrower than the real shape, which
 * is why the row is read through a cast here — the same pattern
 * useOrgOnboardingStatus uses.
 */
export const useOrgProfileStrength = (): UseOrgProfileStrength => {
  const { org, effectiveOrg, isLoading: orgLoading, refetchResolved } = useUserOrg();
  const {
    memberCount,
    isLoading: membersLoading,
    refetch: refetchMemberCount,
  } = useOrgMemberCount();

  const orgId = org?.id ?? null;

  const refresh = useCallback(async (): Promise<OrgProfileStrength | null> => {
    // Both in flight together; the org row is what a profile save changed, the
    // count is what an invite changed, and a caller cannot know which happened.
    const [freshOrg, freshCount] = await Promise.all([
      refetchResolved(),
      refetchMemberCount(),
    ]);
    if (!freshOrg || freshCount === null) return null;
    return computeOrgProfileStrength(
      freshOrg as unknown as Record<string, unknown>,
      freshCount,
    );
  }, [refetchResolved, refetchMemberCount]);

  const isLoading = orgLoading || (!!orgId && membersLoading);

  // Scored from the row WITH its effective profile (office profiles 2B-4,
  // D8): a managed office's inherited groups count, and a group it does not
  // offer counts as answered. For every other organization the effective row
  // is its own data, so the score is unchanged. No effective row means no
  // score, never a score of the own row.
  if (!org || !effectiveOrg || isLoading) {
    return {
      strength: null,
      isReady: false,
      isPublished: false,
      isLoading,
      orgId,
      memberCount: 0,
      refresh,
    };
  }

  // useUserOrg's exported UserOrg interface is narrower than the row it actually
  // selects ("*"), so widen rather than reach for `any`.
  const row = effectiveOrg as unknown as Record<string, unknown>;
  const strength = computeOrgProfileStrength(row, memberCount);

  return {
    strength,
    isReady: isReadyToPublish(strength),
    isPublished: !!(org as unknown as Record<string, unknown>).is_published,
    isLoading: false,
    orgId,
    memberCount,
    refresh,
  };
};
